import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveSupplier, updateSupplier, type Supplier } from '@/services/suppliers';

interface SupplierModalProps {
  supplier?: Supplier | null;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = { companyName: '', contactName: '', phone: '', email: '' };

export default function SupplierModal({ supplier, onClose, onSaved }: SupplierModalProps) {
  const isEditing = !!supplier;
  const [form, setForm] = useState(
    supplier
      ? {
          companyName: supplier.companyName,
          contactName: supplier.contactName,
          phone: supplier.phone,
          email: supplier.email,
        }
      : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof typeof emptyForm>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.companyName.trim()) {
      setError('El nombre de la empresa es obligatorio.');
      return;
    }

    setSaving(true);
    const { error: saveError } =
      isEditing && supplier
        ? await updateSupplier(supplier.id, form)
        : await saveSupplier(form);
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar Proveedor' : 'Nuevo Proveedor'}
      subtitle={isEditing ? `Editando: ${supplier?.companyName}` : 'Registra un proveedor de insumos.'}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Empresa *</label>
          <input className={inputCls} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} placeholder="Textiles y Confecciones Bogotá SAS" />
        </div>
        <div>
          <label className={labelCls}>Persona de contacto</label>
          <input className={inputCls} value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="María Fernanda Pérez" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Teléfono</label>
            <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+57 3xx xxx xxxx" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="ventas@empresa.com" />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear proveedor'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}